import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { AuthResponse, ApiResponse, User } from '../types';

class ApiService {
  private api: AxiosInstance;
  private refreshTokenPromise: Promise<void> | null = null;
  private deviceFingerprint: string | null = null;
  private csrfToken: string | null = null;
  private fingerprintPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true // Importante para cookies
    });

    this.setupInterceptors();
    this.initializeFingerprint();
    this.fetchCSRFToken();
  }

  private async initializeFingerprint() {
    if (this.fingerprintPromise) {
      return this.fingerprintPromise;
    }

    this.fingerprintPromise = this.generateFingerprint();
    return this.fingerprintPromise;
  }

  private async generateFingerprint(): Promise<string> {
    try {
      // Fingerprinting avanzado con FingerprintJS moderna
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.deviceFingerprint = result.visitorId;
      console.log('✅ Fingerprint avanzado generado exitosamente');
      return this.deviceFingerprint;
    } catch (error) {
      console.error('Error generando fingerprint avanzado:', error);
      // Fallback al fingerprint básico
      this.deviceFingerprint = this.generateBasicFingerprint();
      console.log('⚠️ Usando fingerprint básico como fallback');
      return this.deviceFingerprint;
    }
  }

  // Método público para esperar que el fingerprint esté listo
  async waitForFingerprint(): Promise<string> {
    if (this.deviceFingerprint) {
      return this.deviceFingerprint;
    }
    
    if (this.fingerprintPromise) {
      return this.fingerprintPromise;
    }
    
    return this.initializeFingerprint();
  }

  private generateBasicFingerprint(): string {
    const screen = `${window.screen.width}x${window.screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;
    const platform = navigator.platform;
    const userAgent = navigator.userAgent;
    
    const fingerprintData = `${screen}-${timezone}-${language}-${platform}-${userAgent}`;
    
    // Hash simple
    let hash = 0;
    for (let i = 0; i < fingerprintData.length; i++) {
      const char = fingerprintData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return Math.abs(hash).toString(36);
  }

  private async fetchCSRFToken() {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/csrf-token`,
        { withCredentials: true }
      );
      this.csrfToken = response.data.csrfToken;
      console.log('✅ CSRF token obtenido exitosamente');
    } catch (error: any) {
      // Si el endpoint no existe (404), continuar sin CSRF token (modo desarrollo)
      if (error.response?.status === 404) {
        console.warn('⚠️ Endpoint CSRF no disponible - funcionando sin protección CSRF (modo desarrollo)');
        this.csrfToken = null;
      } else {
        console.error('❌ Error obteniendo CSRF token:', error.message);
        this.csrfToken = null;
      }
    }
  }

  private setupInterceptors() {
    // Request interceptor - agregar CSRF token
    this.api.interceptors.request.use(
      (config) => {
        // Añadir CSRF token a las peticiones que lo necesiten
        if (this.csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
          config.headers['X-CSRF-Token'] = this.csrfToken;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - manejar refresh token y CSRF
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Si recibimos error de CSRF, obtener nuevo token
        if (error.response?.status === 403 && error.response?.data?.code === 'EBADCSRFTOKEN') {
          await this.fetchCSRFToken();
          originalRequest.headers['X-CSRF-Token'] = this.csrfToken;
          return this.api(originalRequest);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshAccessToken();
            return this.api(originalRequest);
          } catch (refreshError) {
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = this.performTokenRefresh();
    
    try {
      await this.refreshTokenPromise;
    } finally {
      this.refreshTokenPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<void> {
    const response = await this.api.post('/auth/refresh', {
      fingerprint: this.deviceFingerprint
    });

    if (!response.data.success) {
      throw new Error('Failed to refresh token');
    }
  }

  // Autenticación actualizada para cookies HTTP-only
  async login(ruc: string, password: string, recaptcha_token?: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/login', {
      ruc,
      password,
      recaptcha_token,
      fingerprint: this.deviceFingerprint
    });

    if (response.data.success) {
      // Los tokens ahora se manejan como cookies httpOnly
      // Solo guardamos información del usuario
      sessionStorage.setItem('user', JSON.stringify(response.data.data.user));
    }

    return response.data;
  }

  async refreshToken(): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/auth/refresh', {
      fingerprint: this.deviceFingerprint
    });

    if (response.data.success) {
      sessionStorage.setItem('user', JSON.stringify(response.data.data.user));
    }

    return response.data;
  }

  logout(): void {
    // Llamar al endpoint de logout para limpiar cookies
    this.api.post('/auth/logout').catch(console.error);
    sessionStorage.removeItem('user');
  }

  getCurrentUser(): User | null {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated(): boolean {
    return !!sessionStorage.getItem('user');
  }

  // ==================== COMERCIOS (UNIFICADO) ====================

  // Comercios CRUD (Admin)
  async getComercios(params?: {
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
    buscar?: string;
    departamento_id?: string;
    provincia_id?: string;
    distrito_id?: string;
    activo?: boolean;
    con_locales?: boolean;
    con_campañas_activas?: boolean;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/admin/comercios', { params });
    return response.data;
  }

  async getComercio(id: number): Promise<ApiResponse> {
    const response = await this.api.get(`/admin/comercios/${id}`);
    return response.data;
  }

  async createComercio(data: {
    ruc: string;
    razon_social: string;
    email: string;
    telefono?: string;
    password?: string;
    direccion_principal: {
      calle: string;
      numero?: string;
      departamento_id: string;
      provincia_id: string;
      distrito_id: string;
      referencia?: string;
    };
    horario_atencion: any;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/admin/comercios', data);
    return response.data;
  }

  async updateComercio(id: number, data: any): Promise<ApiResponse> {
    const response = await this.api.put(`/admin/comercios/${id}`, data);
    return response.data;
  }

  async deleteComercio(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/comercios/${id}`);
    return response.data;
  }

  async toggleComercio(id: number): Promise<ApiResponse> {
    const response = await this.api.patch(`/admin/comercios/${id}/toggle`);
    return response.data;
  }

  async resetComercioPassword(id: number): Promise<ApiResponse> {
    const response = await this.api.post(`/admin/comercios/${id}/reset-password`);
    return response.data;
  }

  async getDashboard(): Promise<ApiResponse> {
    const response = await this.api.get('/admin/dashboard');
    return response.data;
  }

  // ==================== ALIASES PARA COMPATIBILIDAD ====================

  // Métodos alias para mantener compatibilidad con el frontend existente
  // (Eliminados: usar solo los métodos de /admin/comercios)

  // ==================== COMERCIO (PANEL DE COMERCIO) ====================

  async getPerfilComercio(): Promise<ApiResponse> {
    const response = await this.api.get('/comercio/perfil');
    return response.data;
  }

  async getLocalesComercio(): Promise<ApiResponse> {
    const response = await this.api.get('/comercio/locales');
    return response.data;
  }

  async getCampañasActivas(): Promise<ApiResponse> {
    const response = await this.api.get('/comercio/campañas/activas');
    return response.data;
  }

  async getRedenciones(params?: {
    page?: number;
    limit?: number;
    estado?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/comercio/redenciones', { params });
    return response.data;
  }

  async procesarRedencion(id: number, data: {
    estado: 'aprobado' | 'rechazado';
    observaciones?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/comercio/redenciones/${id}`, data);
    return response.data;
  }

  async getEstadisticasComercio(): Promise<ApiResponse> {
    const response = await this.api.get('/comercio/estadisticas');
    return response.data;
  }

  // ==================== CUPONES ====================

  async getCupones(params?: {
    page?: number;
    limit?: number;
    convenio_id?: number;
    tipo?: string;
    activo?: boolean;
    wordpress_user_id?: number;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/cupones', { params });
    return response.data;
  }

  async createCuponIndividual(data: {
    convenio_id: number;
    wordpress_user_id: number;
    usos_maximos: number;
    expires_at?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/cupones/individual', data);
    return response.data;
  }

  async createCuponesMasivos(data: {
    convenio_id: number;
    usos_maximos: number;
    cantidad: number;
    expires_at?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/cupones/masivos', data);
    return response.data;
  }

  async getCuponQR(codigo: string, format: 'dataurl' | 'buffer' | 'svg' = 'dataurl'): Promise<string> {
    const response = await this.api.get(`/cupones/${codigo}/qr`, {
      params: { format },
      responseType: format === 'dataurl' ? 'json' : 'blob'
    });

    if (format === 'dataurl') {
      return response.data.data.qr_data;
    } else {
      return URL.createObjectURL(response.data);
    }
  }

  async validateCupon(codigo: string, timestamp: string, hash: string): Promise<ApiResponse> {
    const response = await this.api.post(`/cupones/${codigo}/validar`, {}, {
      params: { t: timestamp, h: hash }
    });
    return response.data;
  }

  async toggleCupon(id: number): Promise<ApiResponse> {
    const response = await this.api.patch(`/cupones/${id}/toggle`);
    return response.data;
  }

  async getCuponesUsuario(userId: number): Promise<ApiResponse> {
    const response = await this.api.get(`/cupones/usuario/${userId}`);
    return response.data;
  }

  async getCuponStats(): Promise<ApiResponse> {
    const response = await this.api.get('/cupones/stats/general');
    return response.data;
  }

  // ==================== VERIFICACIÓN POR DOCUMENTO ====================

  async validarDocumento(data: {
    tipo_documento: 'dni' | 'carnet_extranjeria' | 'pasaporte';
    numero_documento: string;
    comercio_id: number;
    monto_consumo: number;
    recaptcha_token?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/documento/validar', data);
    return response.data;
  }

  async redimirDocumento(data: {
    tipo_documento: string;
    numero_documento: string;
    convenio_id: number;
    monto_consumo: number;
    descuento_aplicado: number;
    observaciones?: string;
    recaptcha_token?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/documento/redimir', data);
    return response.data;
  }

  async getUsuarioPorDocumento(tipo: string, numero: string): Promise<ApiResponse> {
    const response = await this.api.get(`/documento/usuario/${tipo}/${numero}`);
    return response.data;
  }

  async getEstadisticasDocumento(): Promise<ApiResponse> {
    const response = await this.api.get('/documento/estadisticas');
    return response.data;
  }

  async getConveniosComercio(comercioId: number): Promise<ApiResponse> {
    const response = await this.api.get(`/documento/convenios/${comercioId}`);
    return response.data;
  }

  async validarFormatoDocumento(tipo: string, numero: string): Promise<ApiResponse> {
    const response = await this.api.get(`/documento/validar-formato/${tipo}/${numero}`);
    return response.data;
  }

  // ==================== UBIGEOS ====================

  async getDepartamentos(): Promise<ApiResponse> {
    const response = await this.api.get('/ubigeos/departamentos');
    return response.data;
  }

  async getProvincias(departamentoId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/ubigeos/provincias/${departamentoId}`);
    return response.data;
  }

  async getDistritos(provinciaId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/ubigeos/distritos/${provinciaId}`);
    return response.data;
  }

  async getUbicacionCompleta(departamentoId: string, provinciaId: string, distritoId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/ubigeos/ubicacion/${departamentoId}/${provinciaId}/${distritoId}`);
    return response.data;
  }

  async validarUbigeo(data: {
    departamento_id: string;
    provincia_id: string;
    distrito_id: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/ubigeos/validar', data);
    return response.data;
  }

  async buscarUbicaciones(termino: string, limite?: number): Promise<ApiResponse> {
    const response = await this.api.get('/ubigeos/buscar', { 
      params: { q: termino, limite } 
    });
    return response.data;
  }

  async getUbigeosPopulares(): Promise<ApiResponse> {
    const response = await this.api.get('/ubigeos/populares');
    return response.data;
  }

  // ==================== REPORTES ====================

  async getReporteCupones(params?: {
    fecha_desde?: string;
    fecha_hasta?: string;
    comercio_id?: number;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/admin/reportes/cupones', { params });
    return response.data;
  }

  async getReporteRedenciones(params?: {
    fecha_desde?: string;
    fecha_hasta?: string;
    comercio_id?: number;
  }): Promise<ApiResponse> {
    const response = await this.api.get('/admin/reportes/redenciones', { params });
    return response.data;
  }

  // ==================== LOCALES Y CAMPAÑAS (FUTURO) ====================

  // Estos métodos están preparados para implementación futura
  async getLocales(comercioId: number, params?: {
    activo?: boolean;
    departamento_id?: string;
    provincia_id?: string;
    distrito_id?: string;
  }): Promise<ApiResponse> {
    // Por ahora retorna los locales del comercio
    const comercio = await this.getComercio(comercioId);
    if (comercio.success) {
      return {
        success: true,
        data: comercio.data.locales || []
      };
    }
    return comercio;
  }

  async createLocal(comercioId: number, data: {
    nombre: string;
    direccion: {
      calle: string;
      numero?: string;
      departamento_id: string;
      provincia_id: string;
      distrito_id: string;
      referencia?: string;
    };
    hereda_horario: boolean;
    horario_propio?: any;
  }): Promise<ApiResponse> {
    // Nuevo endpoint unificado
    const response = await this.api.post(`/admin/comercios/${comercioId}/locales`, data);
    return response.data;
  }

  async updateLocal(comercioId: number, localId: number, data: any): Promise<ApiResponse> {
    const response = await this.api.put(`/admin/comercios-ext/${comercioId}/locales/${localId}`, data);
    return response.data;
  }

  async deleteLocal(comercioId: number, localId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/comercios-ext/${comercioId}/locales/${localId}`);
    return response.data;
  }

  async getCampañas(params?: {
    comercio_id?: number;
    activo?: boolean;
    vigente?: boolean;
    local_id?: number;
    aplica_todo_negocio?: boolean;
  }): Promise<ApiResponse> {
    // Por ahora retorna las campañas del comercio especificado
    if (params?.comercio_id) {
      const comercio = await this.getComercio(params.comercio_id);
      if (comercio.success) {
        return {
          success: true,
          data: comercio.data.campañas || []
        };
      }
      return comercio;
    }
    
    return {
      success: false,
      message: 'comercio_id es requerido'
    };
  }

  async getCampañasComercio(comercioId: number, params?: {
    activo?: boolean;
    vigente?: boolean;
    local_id?: number;
  }): Promise<ApiResponse> {
    const response = await this.api.get(`/comercios-ext/${comercioId}/campanas`, { params });
    return response.data;
  }

  async createCampaña(comercioId: number, data: {
    nombre: string;
    descripcion: string;
    fecha_inicio: string;
    fecha_fin: string;
    aplica_todo_negocio: boolean;
    locales_aplicables?: number[];
    consumo_minimo?: number;
    descuento_maximo?: number;
    porcentaje_descuento?: number;
    monto_descuento?: number;
    numero_promociones: number;
  }): Promise<ApiResponse> {
    // Nuevo endpoint unificado
    const response = await this.api.post(`/admin/comercios/${comercioId}/campanas`, data);
    return response.data;
  }

  async updateCampaña(comercioId: number, campañaId: number, data: {
    nombre: string;
    descripcion: string;
    fecha_inicio: string;
    fecha_fin: string;
    aplica_todo_negocio: boolean;
    locales_aplicables?: number[];
    consumo_minimo?: number;
    descuento_maximo?: number;
    porcentaje_descuento?: number;
    monto_descuento?: number;
    numero_promociones: number;
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/admin/comercios-ext/${comercioId}/campanas/${campañaId}`, data);
    return response.data;
  }

  async deleteCampaña(comercioId: number, campañaId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/comercios-ext/${comercioId}/campanas/${campañaId}`);
    return response.data;
  }

  // Método genérico para requests personalizados
  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.api.request(config);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
